import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ExpertiseList } from '@/ui/components/molecules/ExpertiseList'
import { ExpertiseStatus } from '@/ui/components/molecules/ExpertiseCard/ExpertiseCard.types'
import { ControlsContainer } from '@/ui/components/molecules/ControlsContainer'
import { Button } from '@/ui/components/atoms/Button'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useExpertisesList } from '../../hooks/useExpertisesList'
import { Customer } from '../../types/search.types'
import { getFilterPills, getSortOptions } from '../../utils/searchHelpers'
import './ExpertisesList.styles.css'

interface ExpertisesListProps {
  customer: Customer
  onBack: () => void
}

export const ExpertisesList: React.FC<ExpertisesListProps> = ({ customer, onBack }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const basePath = location.pathname.includes('/bureau') ? '/bureau' : '/admin'
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [activeFilter, setActiveFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  const { expertises, loading, error } = useExpertisesList(customer.fmClientId)

  const filteredAndSortedExpertises = useMemo(() => {
    return expertises
      .filter(exp => {
        if (activeFilter === 'all') return true
        return exp.category === activeFilter
      })
      .filter(exp => {
        if (!searchQuery.trim()) return true
        const query = searchQuery.toLowerCase()
        return (
          exp.type.toLowerCase().includes(query) ||
          exp.address.join(' ').toLowerCase().includes(query) ||
          exp.expert.toLowerCase().includes(query) ||
          exp.status.toLowerCase().includes(query)
        )
      })
      .sort((a, b) => {
        let result = 0
        switch (sortBy) {
          case 'date':
            result = new Date(a.date).getTime() - new Date(b.date).getTime()
            break
          case 'type':
            result = a.type.localeCompare(b.type)
            break
          case 'status': {
            const statusOrder: Record<string, number> = {
              'En cours': 1,
              'En attente': 2,
              'Terminé': 3
            }
            result = (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4)
            break
          }
          default:
            result = 0
        }
        // Apply sort order
        return sortOrder === 'desc' ? -result : result
      })
  }, [expertises, activeFilter, searchQuery, sortBy, sortOrder])

  const handleExpertiseClick = useCallback((expertise: any) => {
    // Navigate to expertise detail using the expertise ID
    const expertiseId = expertise.id || expertise.affaire_id
    navigate(`${basePath}/affaire/${expertiseId}`, {
      state: {
        from: `${basePath}/search`,
        searchContext: {
          tab: 'customer',
          searchQuery: `${customer.firstName} ${customer.lastName}`,
          selectedCustomer: customer
        }
      }
    })
  }, [navigate, customer, basePath])

  const handleFileMakerClick = useCallback((expertise: any) => {
    // Open FileMaker link
    const fmUrl = `fmp://$/fm2.chatelainxpert.be/ChatelainXpert.fmp12?script=mondaytofm&param=${expertise.id}`
    window.location.href = fmUrl
  }, [])

  // Transform expertises to match ExpertiseList component format
  const transformedExpertises = useMemo(() => {
    return filteredAndSortedExpertises.map(expertise => ({
      id: expertise.id,
      reference: expertise.id,
      title: expertise.type,
      status: (expertise.status === 'En cours' ? 'en_cours' : expertise.status.toLowerCase()) as ExpertiseStatus,
      statusDisplay: expertise.status || 'status not found', // Pass raw backend status
      clientName: customer.firstName + ' ' + customer.lastName,
      expertName: expertise.expert,
      address: {
        street: expertise.address[0] || '',
        city: expertise.address[1] || '',
        postalCode: ''
      },
      progress: expertise.completionPercentage,
      priority: expertise.priority as 'low' | 'medium' | 'high' | 'urgent',
      tags: expertise.tags,
      createdAt: expertise.date,
      dueDate: undefined,
      isUrgent: expertise.priority === 'urgent',
      showProgress: true,
      _originalData: expertise
    }))
  }, [filteredAndSortedExpertises, customer])

  return (
    <div className="expertises-page">
      {/* Custom header with button on the left */}
      <div className="expertises-page__header">
        <Button
          variant="secondary"
          size="medium"
          icon={<ArrowLeftIcon className="w-5 h-5" />}
          onClick={onBack}
          className="expertises-page__back-button"
        >
          Retour
        </Button>
        
        <div className="expertises-page__header-content">
          <h1 className="expertises-page__title">
            Expertises pour {customer.firstName} {customer.lastName}
          </h1>
          <p className="expertises-page__subtitle">
            {customer.email || 'Email non disponible'}
          </p>
        </div>
        
        <div className="expertises-page__actions">
          <span className="fm-client-id">FM ID: {customer.fmClientId}</span>
          <span className={`account-status ${customer.hasAccount ? 'account-status--active' : 'account-status--inactive'}`}>
            {customer.hasAccount ? 'Compte actif' : 'Client FileMaker uniquement'}
          </span>
        </div>
      </div>
      <div className="search-content">
        <ControlsContainer
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          filterOptions={getFilterPills()}
          showFilters={true}
          showResults={!loading && !error}
          resultsCount={filteredAndSortedExpertises.length}
          searchPlaceholder="Rechercher dans les expertises..."
          sortOptions={getSortOptions()}
          className="expertises-controls"
        />

        {/* Use ExpertiseList component like in pipeline */}
        <ExpertiseList
          expertises={transformedExpertises}
          onExpertiseClick={(expertise) => {
            const originalData = (expertise as any)._originalData
            handleExpertiseClick(originalData)
          }}
          onFileMakerClick={(expertise) => {
            const originalData = (expertise as any)._originalData
            handleFileMakerClick(originalData)
          }}
          className="search-expertise-list"
          showFacadeImage={true}
          showProgress={true}
          showActions={true}
          isSearchActive={!!searchQuery.trim()}
          loading={loading}
          error={error}
          emptyStateTitle="Aucune expertise trouvée"
          emptyStateMessage={`Aucune expertise n'a été trouvée pour ${customer.firstName} ${customer.lastName}.`}
        />
      </div>
    </div>
  )
}